/**
 * sharedMailboxTarget — module store + hook combining the shared-mailbox
 * feature flag with `detectTargetMailbox()` auto-detection. Vanilla pub/sub
 * store (the `workflowStore.ts` singleton pattern) so the launch target is
 * readable from a plain function at the `orchestratorApi.triggerWorkflow`
 * injection point, which is not a React context.
 *
 * There is NO manual picker and NO "Acting on" confirmation (both removed per
 * operator decision): a shared launch is threaded silently, so the experience
 * is identical to a personal launch. A shared target is only ever the address
 * `detectTargetMailbox()` auto-detected from the current Outlook message's
 * shared-folder context; surfaces that can't auto-detect resolve to "personal".
 * The launch is briefly gated ONLY while the flag/detection round-trip is in
 * flight ("pending") so a shared launch can never silently fall back to
 * personal because the user clicked before detection resolved.
 *
 * The server (product API) trusts this client target for the pilot; it stays a
 * client hint, never an authorization decision.
 */
import { useEffect, useState } from "react";
import { subscribeItemChanged } from "../../outlook/itemChanged";
import { orchestratorApi } from "../../services/orchestratorApi";
import { detectTargetMailbox } from "./detectTargetMailbox";
import type { SharedMailboxDetection } from "./detectTargetMailbox";

/**
 * Single client-side definition of the flag key (mirrors the API/UI constant).
 *
 * KILL SWITCH — shared-mailbox launch is a DEFAULT-ON feature. The
 * `shared-mailbox-disabled` flag is NEGATIVE: an ENABLED row pulls the feature
 * back (no detection, launches behave as personal). Absent/disabled row ⇒ the
 * feature is ON, so `flagEnabled` below is `!isEnabled`.
 */
export const SHARED_MAILBOX_DISABLED_FLAG = "shared-mailbox-disabled";

export type SharedMailboxLaunchStatus =
  | "off"
  | "pending"
  | "personal"
  | "shared";

interface SharedMailboxState {
  flagEnabled: boolean;
  flagLoaded: boolean;
  detection: SharedMailboxDetection | null;
}

type Subscriber = (state: SharedMailboxState) => void;

function initialState(): SharedMailboxState {
  return {
    flagEnabled: false,
    flagLoaded: false,
    detection: null,
  };
}

let _state: SharedMailboxState = initialState();
const _subscribers = new Set<Subscriber>();

function _setState(patch: Partial<SharedMailboxState>): void {
  _state = { ..._state, ...patch };
  _subscribers.forEach((fn) => fn(_state));
}

function _subscribe(fn: Subscriber): () => void {
  _subscribers.add(fn);
  return () => {
    _subscribers.delete(fn);
  };
}

/**
 * Non-null ONLY when the flag is enabled AND detection auto-resolved a shared
 * target. Read as a plain function — not a hook — from the
 * `orchestratorApi.triggerWorkflow` merge point, which is outside React. A
 * personal/off/pending state returns null, so `target_mailbox` is ABSENT from
 * the launch payload (byte-identical to today's personal launch).
 */
export function getSharedMailboxLaunchTarget(): {
  targetMailbox: string;
} | null {
  if (!_state.flagEnabled || _state.detection?.kind !== "shared") return null;
  return { targetMailbox: _state.detection.targetMailbox };
}

/**
 * Single shared predicate for whether a launch must be blocked RIGHT NOW. Both
 * LaunchTab's catalog and the Active tab's catalog (App.tsx) gate identically:
 * the ONLY gate is "pending" — the flag GET or the detection round-trip is still
 * in flight, so we don't yet know whether this is a shared or personal launch.
 * Blocking that sub-second window prevents a shared launch from slipping through
 * ungated as personal. Once resolved (shared/personal/off) launches proceed
 * normally; a shared target is threaded automatically at triggerWorkflow.
 */
export function isSharedMailboxLaunchGated(
  status: SharedMailboxLaunchStatus,
): boolean {
  return status === "pending";
}

/** Test-only reset — mirrors `workflowStore._reset()`. */
export function _resetSharedMailboxStore(): void {
  _state = initialState();
  _subscribers.clear();
}

export interface UseSharedMailboxTargetResult {
  status: SharedMailboxLaunchStatus;
  /** The auto-detected shared address, else null. */
  targetMailbox: string | null;
}

/**
 * Combines flag state with `detectTargetMailbox()`. Fetches the flag once;
 * runs detection ONLY when the flag is ON (mount + every Office `ItemChanged`,
 * through the pane's one subscription in `outlook/itemChanged.ts`).
 * `ItemChanged` re-detects — the target always tracks whichever message is open.
 */
export function useSharedMailboxTarget(): UseSharedMailboxTargetResult {
  const [state, setState] = useState<SharedMailboxState>(_state);

  useEffect(() => _subscribe(setState), []);

  // 1. Fetch the kill switch once. NEGATIVE flag: an enabled row means the
  // feature is KILLED, so featureEnabled = !isEnabled. Absent/disabled row (the
  // default) or a fail-closed isEnabled:false on lookup error ⇒ feature ON.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { isEnabled } = await orchestratorApi.getFeatureFlag(SHARED_MAILBOX_DISABLED_FLAG);
      if (!cancelled) _setState({ flagEnabled: !isEnabled, flagLoaded: true });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. Detect ONLY when the flag is ON. Never invoked on the legacy
  // (flag-OFF) path — no Office.js shared-properties call at all.
  useEffect(() => {
    if (!state.flagLoaded || !state.flagEnabled) return;
    let cancelled = false;

    const runDetect = () => {
      // Invalidate any prior detection SYNCHRONOUSLY, before the async Office.js
      // round-trip — the in-flight window must read as "pending" (launch gated),
      // never as a stale detection or an implicit "personal". Without this, an
      // ItemChanged re-detect leaves the previous message's target live until
      // getSharedPropertiesAsync resolves.
      _setState({ detection: null });
      void detectTargetMailbox().then((detection) => {
        if (cancelled) return;
        _setState({ detection });
      });
    };

    runDetect();
    // Never a handler of our own: Outlook's removeHandlerAsync removes EVERY
    // ItemChanged handler, so this hook's cleanup used to unsubscribe
    // `useMailboxItem` too (story 28633).
    const unsubscribe = subscribeItemChanged(runDetect);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [state.flagLoaded, state.flagEnabled]);

  // A null detection NEVER reads as "personal". Until the flag GET resolves the
  // status is "pending" (fail-closed — a click in that window must not slip
  // through ungated); once the flag is known ON but detection is still in flight
  // (mount or ItemChanged re-detect), it stays "pending".
  const status: SharedMailboxLaunchStatus = !state.flagLoaded
    ? "pending"
    : !state.flagEnabled
      ? "off"
      : (state.detection?.kind ?? "pending");

  const targetMailbox =
    state.detection?.kind === "shared" ? state.detection.targetMailbox : null;

  return { status, targetMailbox };
}
